// Privacy Policy Management Tab
// Date: January 27, 2025

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { contentManagementService, PrivacyPolicy } from '@/services/contentManagementService';
import { toast } from 'react-hot-toast';
import { CheckIcon, ClockIcon } from '@heroicons/react/24/outline';

export const PrivacyPolicyTab: React.FC = () => {
  const [history, setHistory] = useState<PrivacyPolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState('');
  const [version, setVersion] = useState('');

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      setLoading(true);
      const data = await contentManagementService.getPrivacyPolicyHistory();
      setHistory(data);
      const active = data.find(p => p.is_active);
      if (active) {
        setContent(active.content_html);
        setVersion(active.version);
      }
    } catch (error: any) {
      console.error('Error loading privacy policy:', error);
      toast.error(error.response?.data?.message || 'Failed to load privacy policy');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!content.trim() || !version.trim()) {
      toast.error('Content and version are required');
      return;
    }

    try {
      setSaving(true);
      await contentManagementService.createOrUpdatePrivacyPolicy({
        content_html: content,
        version: version,
      });
      toast.success('Privacy policy saved successfully');
      await loadHistory();
    } catch (error: any) {
      console.error('Error saving privacy policy:', error);
      toast.error(error.response?.data?.message || 'Failed to save privacy policy');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-accent-bg)]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Edit Privacy Policy</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
              Version
            </label>
            <input
              type="text"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="e.g., 1.0.0"
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-bg)]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
              Content (HTML)
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={20}
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-bg)] font-mono text-sm"
              placeholder="Enter HTML content for privacy policy..."
            />
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full sm:w-auto"
          >
            {saving ? 'Saving...' : 'Save Privacy Policy'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Version History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {history.length === 0 ? (
              <p className="text-sm text-[var(--color-secondary-text)] text-center py-4">
                No privacy policy versions found
              </p>
            ) : (
              history.map((policy) => (
                <div
                  key={policy.id}
                  className="flex items-center justify-between p-4 border border-[var(--color-border)] rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    {policy.is_active ? (
                      <CheckIcon className="w-5 h-5 text-green-600" />
                    ) : (
                      <ClockIcon className="w-5 h-5 text-gray-400" />
                    )}
                    <div>
                      <p className="font-medium text-[var(--color-surface-text)]">
                        Version {policy.version}
                      </p>
                      <p className="text-xs text-[var(--color-secondary-text)]">
                        {new Date(policy.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  {policy.is_active && (
                    <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">
                      Active
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

